import { mapUserFromDTO } from '@entities/user/api/mappers'
import { dateToIsoString } from '@shared/lib/dateToIsoString/dateToIsoString'
import type {
    AdLink,
    AdLinkDTO,
    AdStatistics,
    AdStatisticsDTO,
    AdvertisingInfo,
    AdvertisingInfoDTO,
    Message,
    MessageDTO,
    OrdInfo,
    OrdInfoDTO,
    Post,
    PostDTO
} from '../model/types'
import { MessageType } from '../model/types'
import type {
    AdvertisingInfoCreateArgs,
    AdvertisingInfoCreateArgsDTO,
    CreatePostArgs,
    CreatePostArgsDTO
} from './types'

export const mapPostFromDTO = ({
    id,
    title,
    creator,
    state,
    channels,
    messages,
    publish_date,
    created_at,
    updated_at,
    is_shared,
    auto_delete_seconds,
    is_pinned,
    notify,
    is_advertising,
    advertising_info,
    custom_post_type,
    keep_on_top_seconds
}: PostDTO): Post => ({
    id,
    title,
    creator: mapUserFromDTO(creator),
    state,
    channels,
    messages: messages.map(mapMessageFromDTO),
    publishDate: publish_date ? new Date(publish_date) : undefined,
    autoDeleteHours: auto_delete_seconds ? auto_delete_seconds / 3600 : undefined,
    keepOnTopHours: keep_on_top_seconds ? keep_on_top_seconds / 3600 : undefined,
    isPinned: is_pinned,
    notify,
    createdAt: new Date(created_at),
    updatedAt: new Date(updated_at),
    isShared: is_shared,
    isAdvertising: is_advertising,
    advertisingInfo: advertising_info && mapAdvertisingInfoFromDTO(advertising_info),
    postType: {
        value: custom_post_type
    }
})

export const mapMessageFromDTO = ({
    id,
    text,
    message_type,
    files,
    quiz,
    show_caption_above_media,
    repost_link,
    reply_link
}: MessageDTO): Message => ({
    id,
    text,
    messageType: message_type,
    file: files?.map((item) => ({ response: item })),
    isCaptionAbove: show_caption_above_media,
    options: quiz?.options,
    question: quiz?.question,
    replyLink: reply_link,
    repostLink: repost_link
})

const mapMessageToDTO = ({
    id,
    text,
    messageType,
    file,
    options,
    question,
    isCaptionAbove,
    replyLink,
    repostLink
}: Message): MessageDTO => {
    const response: MessageDTO = {
        id,
        text,
        files: file?.map((item) => item.response),
        show_caption_above_media: isCaptionAbove,
        message_type: file?.length > 1 ? MessageType.ALBUM : messageType,
        reply_link: replyLink,
        repost_link: repostLink
    }

    if (messageType === MessageType.QUIZ) {
        response.quiz = {
            options,
            question
        }
    }
    return response
}

export const mapCreatePostArgsToDTO = ({
    title,
    state,
    channels,
    messages,
    publishDate,
    autoDeleteHours,
    keepOnTopHours,
    isPinned,
    notify,
    isAdvertising,
    advertisingInfo,
    postType
}: CreatePostArgs): CreatePostArgsDTO => ({
    title,
    state,
    channels,
    messages: messages.map(mapMessageToDTO),
    publish_date: dateToIsoString(publishDate),
    auto_delete_seconds: autoDeleteHours ? autoDeleteHours * 3600 : undefined,
    keep_on_top_seconds: keepOnTopHours ? keepOnTopHours * 3600 : undefined,
    is_pinned: isPinned,
    notify: notify,
    is_advertising: isAdvertising,
    advertising_info: advertisingInfo && mapAdvertisingInfoCreateArgsToDTO(advertisingInfo),
    custom_post_type: postType?.value && postType?.value > 0 ? postType?.value : undefined
})

export const mapOrdInfoToDTO = ({
    withAgent,
    category,
    agentCommission,
    resourceName,
    resourceType,
    resourceUrl,
    reallocations,
    nds,
    ndsPercent
}: OrdInfo): OrdInfoDTO => ({
    with_agent: withAgent,
    category,
    agent_commission: agentCommission,
    resource_name: resourceName,
    resource_type: resourceType,
    resource_url: resourceUrl,
    reallocations,
    nds,
    nds_percent: ndsPercent
})

export const mapOrdInfoFromDTO = ({
    with_agent,
    category,
    agent_commission,
    resource_name,
    resource_type,
    resource_url,
    reallocations,
    nds,
    nds_percent
}: OrdInfoDTO): OrdInfo => ({
    withAgent: with_agent,
    category,
    agentCommission: agent_commission,
    resourceName: resource_name,
    resourceType: resource_type,
    resourceUrl: resource_url,
    reallocations,
    nds,
    ndsPercent: nds_percent
})

export const mapAdvertisingInfoCreateArgsToDTO = ({
    adCampaign,
    budget,
    viewToken,
    sellerConfirm,
    customerConfirm,
    paymentConfirmed,
    priceMethod,
    links,
    ordInfo
}: AdvertisingInfoCreateArgs): AdvertisingInfoCreateArgsDTO => ({
    ad_campaign: adCampaign,
    budget,
    view_token: viewToken,
    seller_confirm: sellerConfirm,
    customer_confirm: customerConfirm,
    payment_confirmed: paymentConfirmed,
    price_method: priceMethod,
    links: links?.map(mapAdLinkToDTO),
    ord_info: ordInfo && mapOrdInfoToDTO(ordInfo)
})

export const mapAdvertisingInfoToDTO = ({
    adCampaign,
    budget,
    viewToken,
    views,
    subscribersChange,
    sellerConfirm,
    customerConfirm,
    paymentConfirmed,
    isEditing,
    priceMethod,
    statistics,
    partner,
    internetResource,
    links,
    ordInfo
}: AdvertisingInfo): AdvertisingInfoDTO => ({
    ad_campaign: adCampaign,
    budget,
    view_token: viewToken,
    views,
    subscribers_change: subscribersChange,
    seller_confirm: sellerConfirm,
    customer_confirm: customerConfirm,
    payment_confirmed: paymentConfirmed,
    is_editing: isEditing,
    price_method: priceMethod,
    statistics: mapAdStatisticsToDTO(statistics),
    partner: mapAdPartnerToDTO(partner),
    internet_resource: mapAdInternetResourceToDTO(internetResource),
    links: links?.map(mapAdLinkToDTO),
    ord_info: ordInfo && mapOrdInfoToDTO(ordInfo)
})

export const mapAdvertisingInfoFromDTO = ({
    ad_campaign,
    budget,
    views,
    subscribers_change,
    seller_confirm,
    customer_confirm,
    payment_confirmed,
    is_editing,
    view_token,
    price_method,
    statistics,
    internet_resource,
    partner,
    links,
    ord_info,
    contract_uuid
}: AdvertisingInfoDTO): AdvertisingInfo => ({
    adCampaign: ad_campaign,
    budget,
    views,
    subscribersChange: subscribers_change,
    sellerConfirm: seller_confirm,
    customerConfirm: customer_confirm,
    paymentConfirmed: payment_confirmed,
    isEditing: is_editing,
    viewToken: view_token,
    priceMethod: price_method,
    statistics: mapAdStatisticsFromDTO(statistics),
    internetResource: mapAdInternetResourceFromDTO(internet_resource),
    partner: mapAdPartnerFromDTO(partner),
    links: links?.map(mapAdLinkFromDTO),
    ordInfo: ord_info && mapOrdInfoFromDTO(ord_info),
    contractUuid: contract_uuid
})

export const mapAdStatisticsFromDTO = ({ clicks, joins, leaves }: AdStatisticsDTO): AdStatistics => ({
    clicks,
    joins,
    leaves
})

export const mapAdStatisticsToDTO = ({ clicks, joins, leaves }: AdStatistics): AdStatisticsDTO => ({
    clicks,
    joins,
    leaves
})

export const mapAdLinkFromDTO = ({ text, href, is_unique, generated_href, click_count }: AdLinkDTO): AdLink => ({
    text,
    href,
    isUnique: is_unique,
    generatedHref: generated_href,
    clickCount: click_count
})

export const mapAdLinkToDTO = ({ text, href, isUnique, generatedHref, clickCount }: AdLink): AdLinkDTO => ({
    text,
    href,
    is_unique: isUnique,
    generated_href: generatedHref,
    click_count: clickCount
})

export const mapAdInternetResourceFromDTO = ({
    id,
    name,
    username
}: AdvertisingInfoDTO['internet_resource']): AdvertisingInfo['internetResource'] => ({ id, name, username })

export const mapAdInternetResourceToDTO = ({
    id,
    name,
    username
}: AdvertisingInfo['internetResource']): AdvertisingInfoDTO['internet_resource'] => ({ id, name, username })

export const mapAdPartnerFromDTO = ({
    id,
    name,
    tg_username
}: AdvertisingInfoDTO['partner']): AdvertisingInfo['partner'] => ({ id, name, tgUsername: tg_username })

export const mapAdPartnerToDTO = ({
    id,
    name,
    tgUsername
}: AdvertisingInfo['partner']): AdvertisingInfoDTO['partner'] => ({ id, name, tg_username: tgUsername })
